/**
 * src/pages/respond/SentResponse.js — what a vendor sent, read-only.
 *
 * Shown on the quote link after they send, and every time they open it again,
 * until Marqland closes the inquiry. "Edit my response" (in VendorResponse.js)
 * reopens the form filled in with the same answers.
 */
import React from 'react';
import { FileText, Image as ImageIcon, X } from 'lucide-react';
import { AIR } from '../../styles/airTheme';
import { formatDay } from '../../components/AirDatePicker';
import { AV, PROPERTY_TYPES } from './OffsiteOffer';

const PROPERTY_LABEL = Object.fromEntries(PROPERTY_TYPES);
const AV_LABEL = Object.fromEntries(AV);
const COST_LABEL = { dj: 'DJ', djLicence: 'DJ with licence', licence: 'Licence', banquetHall: 'Banquet hall' };
const ANSWER_LABEL = {
  available: 'Available', can_supply: 'We can supply', not_available: 'Not available',
  cannot_supply: 'We cannot supply', not_our_product: 'Not our product',
};

export const rupees = (n) => (n === undefined || n === null || n === '' ? '' : `₹${Number(n).toLocaleString('en-IN')}`);
export const isImage = (f) => (f?.fileType || '').startsWith('image/') || /\.(jpe?g|png|webp|gif|heic)$/i.test(f?.name || f?.url || '');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const range = (d) => [formatDay(d.checkIn), formatDay(d.checkOut)].filter(Boolean).join(' – ');

const lbl = { fontFamily: AIR.sans, fontWeight: 600, fontSize: 13, color: AIR.ink };
const kv = { margin: '8px 0 0', display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr)', rowGap: 6, columnGap: 12, fontSize: 14 };

/** A file chip: an icon tile with PDF / JPG and the name. With onRemove, a remove button. */
export const FileChip = ({ f, onRemove, tag }) => {
  const ext = (/\.([a-z0-9]+)$/i.exec(f.name || '')?.[1] || (isImage(f) ? 'img' : 'pdf')).toUpperCase().slice(0, 4);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${AIR.line2}`, borderRadius: 10, padding: '6px 10px 6px 6px', background: '#fff', maxWidth: '100%' }}>
      <span style={{ width: 32, height: 32, borderRadius: 6, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', background: isImage(f) ? '#3b6ea8' : '#c13515' }}>
        {isImage(f) ? <ImageIcon size={14} /> : ext}
      </span>
      <a href={f.url} target="_blank" rel="noreferrer" style={{ color: AIR.ink, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{f.name || 'File'}</a>
      {tag && <span style={{ fontSize: 12, color: AIR.goldText, fontWeight: 600 }}>{tag}</span>}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={`Remove ${f.name || 'file'}`}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: AIR.muted, padding: 2, display: 'flex' }}><X size={14} /></button>
      )}
    </span>
  );
};

export const FileList = ({ files, onRemove, isNew }) => (files?.length ? (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {files.map((f, i) => <FileChip key={f.url || i} f={f} onRemove={onRemove ? () => onRemove(i) : null} tag={isNew?.(f) ? 'new' : ''} />)}
  </div>
) : null);

/** One property option as a short read-only card. Prices are per item; there is no total. */
const OptionCard = ({ n, p }) => {
  const rooms = Object.entries(p.roomRates || {}).filter(([, v]) => v).map(([k, v]) => `${k[0].toUpperCase()}${k.slice(1)} ${rupees(v)}`);
  const meals = (p.mealPlans || []).map((m) => (p.mealRates?.[m] ? `${m} ${rupees(p.mealRates[m])}` : m));
  const costs = [
    ...Object.entries(p.costs || {}).filter(([, v]) => v).map(([k, v]) => `${COST_LABEL[k] || k} ${rupees(v)}`),
    ...(p.extras || []).filter((x) => x.label).map((x) => `${x.label}${x.cost ? ` ${rupees(x.cost)}` : ''}`),
  ];
  const rows = [
    ['Dates', (p.dates || []).map((d) => [range(d), d.note].filter(Boolean).join(' · ')).filter(Boolean).join('\n')],
    ['Rooms', rooms.length ? `${rooms.join(' · ')} / night` : ''],
    ['Meals', meals.length ? `${meals.join(' · ')}${Object.keys(p.mealRates || {}).length ? ' / person / day' : ''}` : ''],
    ['Event costs', costs.join(' · ')],
    ['Hall seats', p.hallCapacity ? String(p.hallCapacity) : ''],
    ['AV', (p.av || []).map((a) => AV_LABEL[a] || a).join(', ')],
    ['Taxes', { included: 'GST included', extra: '+ GST' }[p.gst] || ''],
    ['Valid until', formatDay(p.validUntil)],
    ['Cancellation', p.cancellation],
    ['Payment', p.paymentTerms],
    ['Notes', p.notes],
  ].filter(([, v]) => v);
  return (
    <div style={{ border: `1px solid ${AIR.line2}`, borderRadius: 12, padding: '14px 16px', background: '#fff' }}>
      <b style={{ fontSize: 15 }}>{n}. {p.propertyName}</b>
      <div style={{ fontSize: 13, color: AIR.muted }}>{[PROPERTY_LABEL[p.propertyType], p.location].filter(Boolean).join(' · ')}</div>
      {p.link && <a href={p.link} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: AIR.ink }}>Website / brochure</a>}
      <dl style={kv}>
        {rows.map(([k, v]) => (
          <React.Fragment key={k}><dt style={{ color: AIR.muted, fontSize: 13 }}>{k}</dt><dd style={{ margin: 0, whiteSpace: 'pre-line' }}>{v}</dd></React.Fragment>
        ))}
      </dl>
    </div>
  );
};

/** The answers for each product, as a small table. */
const LinesTable = ({ lines }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr>{['Product', 'Answer', 'Price / unit', 'Delivery by', 'Notes'].map((h) => <th key={h} style={{ textAlign: 'left', ...lbl, color: AIR.muted, padding: '8px 10px', borderBottom: `1px solid ${AIR.line2}` }}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i}>
            <td style={{ padding: '10px', borderBottom: `1px solid ${AIR.line}`, fontWeight: 600 }}>{l.name}</td>
            <td style={{ padding: '10px', borderBottom: `1px solid ${AIR.line}` }}>{ANSWER_LABEL[l.status] || l.status}</td>
            <td style={{ padding: '10px', borderBottom: `1px solid ${AIR.line}` }}>{rupees(l.unitPrice) || '—'}</td>
            <td style={{ padding: '10px', borderBottom: `1px solid ${AIR.line}` }}>{formatDay(l.deliveryDate) || '—'}</td>
            <td style={{ padding: '10px', borderBottom: `1px solid ${AIR.line}`, color: AIR.muted }}>{l.notes || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const sentSummary = (r) => {
  const parts = [];
  if (r.lines?.length) parts.push(plural(r.lines.length, 'product'));
  if (r.offsite) parts.push(r.offsite.available === 'no' ? "Can't host this event" : plural(r.offsite.properties?.length || 0, 'property option'));
  if (r.files?.length) parts.push(plural(r.files.length, 'file'));
  return parts.join(' · ');
};

export default function SentResponse({ response: r, vendor }) {
  const props = r.offsite?.properties || [];
  const about = [vendor?.companyName, vendor?.email, [r.contactName, r.phone].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  return (
    <section style={{ background: '#fff', border: `1px solid ${AIR.line2}`, borderRadius: 12, padding: '20px 22px', display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: AIR.serif, fontWeight: 400, fontSize: 26, margin: 0 }}>What you sent</h2>
        <span style={{ fontSize: 13, color: AIR.muted }}>{sentSummary(r)}</span>
      </div>
      {r.offsite?.available === 'no' && <p style={{ margin: 0, fontSize: 15 }}>You told us you can't host this event.</p>}
      {props.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {props.map((p, i) => <OptionCard key={i} n={i + 1} p={p} />)}
        </div>
      )}
      {r.lines?.length > 0 && <LinesTable lines={r.lines} />}
      {r.notes && <div style={{ fontSize: 14 }}><span style={{ ...lbl, color: AIR.muted }}>Anything else: </span>{r.notes}</div>}
      {r.files?.length > 0 && <FileList files={r.files} />}
      {!r.files?.length && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: AIR.muted }}><FileText size={14} /> No files attached.</span>}
      {about && <div style={{ fontSize: 13, color: AIR.muted }}>About you: {about}</div>}
    </section>
  );
}
